import React, { useRef, useEffect, useMemo } from "react";
import {
  Wrapper,
  Container,
  ArchiveContainer,
  ArchiveHeading,
} from "../styles/components/PLP/Navigation";
import NavigationItems from "./Navigation-Items";
import { BREAKPOINTS } from "../styles/variables/Breakpoints";
import gsap from "./Animations";

const PLPNavigation = ({
  collection,
  onNavigate,
  navigationParentRef,
  navigationContainerRef,
  setNavigationScroll,
  activePLP,
}) => {
  const wrapperRef = useRef(null);
  const overflowContainerRef = useRef(null);
  const archiveContainerRef = useRef(null);

  const mm = gsap.matchMedia();

  const navigationSelect = e => {
    const id = e.target.parentElement.getAttribute("id");
    id && onNavigate(id);
  };

  const handleMouseEnter = () => {
    if (!activePLP) return;

    setNavigationScroll(true);

    mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
      gsap.to(navigationContainerRef.current, {
        scaleX: 1,
        scaleY: 1,
        duration: 0.5,
        overwrite: true,
        onComplete: () => {
          navigationParentRef.current.addEventListener("wheel", handleScroll, {
            passive: false,
          });
        },
      });
    });
  };

  const handleMouseLeave = () => {
    setNavigationScroll(false);

    mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
      gsap.to(navigationContainerRef.current, {
        delay: 0.15,
        scaleX: 0.5,
        scaleY: 0.5,
        duration: 0.5,
        overwrite: true,
        onComplete: () => {
          navigationParentRef.current.removeEventListener(
            "wheel",
            handleScroll
          );
        },
      });
    });
  };

  const handleScroll = event => {
    event.preventDefault();

    const scrollAmount = event.deltaY;
    gsap.killTweensOf(navigationParentRef.current);

    mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
      gsap.to(navigationParentRef.current, {
        x: `-=${scrollAmount}`,
        overwrite: true,
        modifiers: {
          x: x => {
            const clampedX = gsap.utils.clamp(
              -(
                navigationParentRef.current.offsetWidth -
                navigationContainerRef.current.offsetWidth
              ),
              0,
              parseFloat(x)
            );
            return `${clampedX}px`;
          },
        },
        duration: 0.5,
        onComplete: () => setNavigationScroll(false),
      });
    });
  };

  const aboutArchiveContent = useMemo(() => {
    return Object.keys(collection).reduce((acc, key) => {
      const item = collection[key];

      if (item.key === "gant--about-section") {
        let archiveContent = item.content.filter(
          content =>
            content.moduleTitle && content.moduleTitle === "about-content"
        );

        archiveContent = archiveContent[0]?.text;

        acc.push({
          key,
          archiveContent,
        });
      }

      return acc;
    }, []);
  }, [collection]);

  const navigationItems = useMemo(() => {
    return Object.keys(collection).reduce((acc, key) => {
      const item = collection[key];

      if (item.key !== "gant--about-section") {
        const pdpPrimaryImages = item.content.filter(
          content =>
            content.moduleTitle && content.moduleTitle === "plp-primary-images"
        );
        const pdpPrimaryImage = pdpPrimaryImages[0]?.images[0];

        const pdpParagraphs = item.content.filter(
          content => content.moduleTitle && content.moduleTitle === "plp-id"
        );

        acc.push({
          key,
          pdpPrimaryImage,
          pdpParagraphsText: pdpParagraphs[0]?.text,
        });
      }

      return acc;
    }, []);
  }, [collection]);

  useEffect(() => {
    if (!navigationContainerRef.current) return;

    mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
      gsap.set(navigationContainerRef.current, {
        scaleX: 0.5,
        scaleY: 0.5,
      });
    });

    return () => {
      navigationParentRef.current.removeEventListener("wheel", handleScroll);
    };
  }, []);

  useEffect(() => {
    wrapperRef.current.classList.toggle("fixed", !!activePLP);
  }, [activePLP]);

  return (
    <Wrapper ref={wrapperRef} $desktop>
      <Container
        ref={navigationContainerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ArchiveContainer id="about-archive">
          <ArchiveHeading onClick={navigationSelect}>
            About The Archive
          </ArchiveHeading>
        </ArchiveContainer>
        <NavigationItems
          overflowContainerRef={overflowContainerRef}
          navigationParentRef={navigationParentRef}
          archiveContainerRef={archiveContainerRef}
          navigationItems={navigationItems}
          aboutArchiveContent={aboutArchiveContent}
          navigationSelect={navigationSelect}
        />
      </Container>
    </Wrapper>
  );
};

export default PLPNavigation;
